/**
 * UserAuthContext — session state for the logged-in user (lawyer or client).
 *
 * Exposes only what the rest of the app actually consumes: { user, loading,
 * logout, refresh }. Login + signup live entirely in AuthHub.jsx (it talks
 * to userApi directly because the OTP-gated signup returns a `pending`
 * intermediate state, not a session). Keeping stale login/signup helpers
 * here previously produced a fragile contract that AuthHub had to bypass.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { clearUserToken, getUserToken, userHeartbeat, userLogout, userMe } from "../lib/userApi";
import { clearAdminToken } from "../lib/adminApi";
import { identify, resetIdentity } from "../lib/analytics";

const UserAuthContext = createContext({
  user: null,
  loading: true,
  logout: async () => {},
  refresh: async () => {},
});

export function UserAuthProvider({ children }) {
  // Synchronous fast-path: if there's no token in localStorage we KNOW the
  // user is logged out — no need to call /auth/me at all. This eliminates
  // the brief "جاري التحقق…" flash between the splash screen and /login.
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => Boolean(getUserToken()));

  const refresh = useCallback(async () => {
    if (!getUserToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await userMe();
      setUser(me);
      // اِربُط جَلسَة PostHog بِالمُستَخدِم (no-op إذا التَّحليلات مُعَطَّلَة).
      if (me?.id) {
        identify(me.id, {
          role: me.role || "lawyer",
          interface_language: "ar",
        });
      }
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Presence heartbeat — fires every 45s while the tab is foregrounded
  // AND immediately when the tab becomes visible (so the admin dashboard
  // sees the user as ONLINE within seconds of them returning).
  useEffect(() => {
    if (!user?.id) return;
    const platform = /Capacitor/i.test(navigator.userAgent)
      ? (/iPhone|iPad|iPod/i.test(navigator.userAgent) ? "iOS" : "Android")
      : "web";

    const ping = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      userHeartbeat(platform);
    };
    ping();
    const iv = setInterval(ping, 45_000);
    const onVis = () => {
      if (!document.hidden) ping();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?.id]);

  const logout = useCallback(async () => {
    await userLogout();
    clearUserToken();
    clearAdminToken();
    setUser(null);
    // أَلغِ رِبط الجَلسَة في PostHog قَبل التَّحويل لِصَفحَة الدُّخول.
    resetIdentity();
    if (typeof window !== "undefined") {
      window.location.assign("/login");
    }
  }, []);

  return (
    <UserAuthContext.Provider value={{ user, loading, logout, refresh }}>
      {children}
    </UserAuthContext.Provider>
  );
}

export const useUser = () => useContext(UserAuthContext);
